import { installRouteRetention } from './data/dayRoutes';
import { installGeocodePurge } from './data/purgeGeocode';
import './theme.css';
import './styles.css';
import './home.css';
import './brief.css';
import './activity.css';
import './signals.css';
import './desk/shell.css';
import './desk/overview.css';
import './desk/split.css';
import './desk/signals.css';
import './desk/dialog.css';
import './desk/pipeline.css';
import './desk/screens.css';
import { setShell } from './app';
import { UNAUTHORIZED_EVENT, clearApiKey, getApiKey } from './data/api';
import { DRAFTS_CHANGED, generateDrafts } from './data/drafts';
import { SIGNALS_CHANGED } from './data/signals';
import { onStoreChange } from './data/store';
import { installSyncTriggers, refreshSyncStatus, runSync } from './data/sync';
import { onBreakpointChange } from './ui/breakpoint';
import { createRouter, currentPath, navigate, rememberNext } from './ui/router';
import { forgetDraftCount } from './ui/sidebarData';
import { mountShell } from './ui/shell';
import { initTheme } from './ui/theme';
import { leadView } from './views/lead';
import { leadFormView } from './views/leadForm';
import { loginView } from './views/login';
import { pipelineView } from './views/pipeline';
import { quickLogView } from './views/quicklog';
import { routeView } from './views/route';
import { routeAreasView } from './views/routeAreas';
import { routePlanView } from './views/routePlan';
import { routeSettingsView } from './views/routeSettings';
import { emailsView } from './views/emails';
import { templatesView } from './views/templatesView';
import { watchlistView } from './views/watchlist';
import { dashboardView } from './views/dashboard';
import { syncStatusView } from './views/syncStatus';
import { moreView } from './views/more';
import { normalizeLegacyPath, legacyDestination } from './ui/legacyRoutes';
import { homeView } from './views/home';
import { briefView } from './views/brief';
import { activityView } from './views/activity';
import { activityFieldsView } from './views/activityFields';
import { signalsView } from './views/signals';
import { sourcesSheetView } from './views/signals/sourcesSheet';
import { importPlanChecksView } from './views/importPlanChecks';

installGeocodePurge();
installRouteRetention();
normalizeLegacyPath();
initTheme();

const root = document.getElementById('app');
if (!root) throw new Error('#app missing');

const shell = mountShell(root);
setShell(shell);

const router = createRouter(
  shell.view,
  [
    { pattern: '/login', view: loginView },
    { pattern: '/home', view: homeView },
    { pattern: '/brief', view: briefView },
    { pattern: '/activity', view: activityView },
    { pattern: '/activity/fields', view: activityFieldsView },
    { pattern: '/signals', view: signalsView },
    { pattern: '/signals/sources', view: sourcesSheetView },
    { pattern: '/import/plan-checks', view: importPlanChecksView },
    { pattern: '/more', view: moreView },
    { pattern: '/log', view: quickLogView },
    { pattern: '/route', view: routeView },
    { pattern: '/route/plan', view: routePlanView },
    { pattern: '/route/areas', view: routeAreasView },
    { pattern: '/route/settings', view: routeSettingsView },
    { pattern: '/emails', view: emailsView },
    { pattern: '/templates', view: templatesView },
    { pattern: '/watchlist', view: watchlistView },
    { pattern: '/dashboard', view: dashboardView },
    { pattern: '/pipeline', view: pipelineView },
    { pattern: '/leads/new', view: leadFormView },
    { pattern: '/leads/:id', view: leadView },
    { pattern: '/leads/:id/edit', view: leadFormView },
    { pattern: '/sync', view: syncStatusView },
  ],
  {
    notFound: () => navigate('#/home', { replace: true }),
    // a screen that could not open: its own title over the error, not the last screen's
    onError: () => {
      shell.setChrome(true);
      shell.setTitle('Could not open', { back: '#/home' });
    },
    // Auth gate (D6): no key → login; with a key, /login bounces home.
    beforeEach: (path) => {
      const hasKey = !!getApiKey();
      if (!hasKey && path !== '/login') {
        rememberNext(location.hash); // e.g. the Send to CRM landing route: sign-in returns there
        return '/login';
      }
      if (hasKey && path === '/login') return '/home';
      if (legacyDestination(path)) return legacyDestination(path);
      return undefined;
    },
    // Reports opened from Pipeline keeps that desktop selection and its existing back link.
    afterRender: (path, query) => {
      shell.setActiveTab(path, query.get('from') === 'pipeline' ? '/pipeline' : path);
      void shell.refreshCounts();
    },
  },
);

window.addEventListener(UNAUTHORIZED_EVENT, () => {
  // the 401s of one moment (sync, drafts, signals at once) sign out once, not once each
  if (!getApiKey()) return;
  clearApiKey();
  shell.toast('API key rejected — sign in again', { kind: 'error' });
  navigate('#/login', { replace: true });
});

/** Screens whose desktop markup differs (D65) re-render when the window crosses 1024 px; the rest
 *  only re-flow, so a form in progress is never rebuilt. */
const LAYOUT_ROUTES = new Set(['/home', '/signals', '/pipeline', '/emails']);
onBreakpointChange(() => {
  if (LAYOUT_ROUTES.has(currentPath())) router.refresh();
  void shell.refreshCounts();
});
onStoreChange(() => void shell.refreshCounts());
window.addEventListener(SIGNALS_CHANGED, () => void shell.refreshCounts());
window.addEventListener(DRAFTS_CHANGED, () => {
  forgetDraftCount();
  void shell.refreshCounts();
});

installSyncTriggers();
// A local database that cannot be read at boot is reported, not fatal: the sync run below puts the
// error on the pill and retries.
void refreshSyncStatus()
  .catch((err: unknown) => console.error('Sync status could not be read', err))
  .then(() => {
    if (getApiKey()) {
      void runSync();
      // M3 (D32): idempotent contract-draft generation on app open — quiet when offline.
      void generateDrafts()
        .then((created) => {
          if (created > 0) {
            shell.toast(`${created} contract email draft${created > 1 ? 's' : ''} ready — tap to open`, {
              kind: 'info',
              duration: 7000,
              onClick: () => navigate('#/emails'),
            });
          }
        })
        .catch(() => undefined);
    }
  });
router.start();

// ---------------------------------------------------------------- service worker (D12)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    let userAcceptedUpdate = false;
    const offerUpdate = (reg: ServiceWorkerRegistration, worker: ServiceWorker) => {
      shell.toast('Update ready — tap to reload', {
        kind: 'info',
        duration: 10_000,
        onClick: () => {
          userAcceptedUpdate = true;
          (reg.waiting ?? worker).postMessage({ type: 'SKIP_WAITING' });
        },
      });
    };
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        // An update may already be sitting in "waiting" from a previous session.
        if (reg.waiting && navigator.serviceWorker.controller) offerUpdate(reg, reg.waiting);
        reg.addEventListener('updatefound', () => {
          const worker = reg.installing;
          if (!worker) return;
          worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) offerUpdate(reg, worker);
          });
        });
      })
      .catch((err: unknown) => console.error('SW registration failed', err));
    // Reload only when the user tapped the update toast — never on first install, never mid-form.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!userAcceptedUpdate || reloading) return;
      reloading = true;
      location.reload();
    });
  });
}
